import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Container, Form, Button, Alert, Image } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import './Login.css';
import { containsEmoji, validatePassword } from '../../../utils/Utils';
import { PASSWORD_VALIDATION } from '../../../utils/Constants';
import { ApiError } from '../../../types/errors';

interface ResetPasswordErrors {
  password?: string;
  confirmPassword?: string;
  auth?: string;
}

interface TokenVerificationResponse {
  name: string;
  email: string;
  message?: string;
}

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [errors, setErrors] = useState<ResetPasswordErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async (): Promise<void> => {
      try {
        const response = await axios.post<TokenVerificationResponse>('/verify-reset-token', { token });
        setIsTokenValid(true);
        setEmail(response.data.email);
        setCompanyName(response.data.name);
      } catch (error: unknown) {
        const axiosError = error as ApiError;
        if (axiosError.response?.data) {
          setErrors({ auth: axiosError.response.data.message || 'トークンの検証に失敗しました' });
        } else {
          setErrors({ auth: 'サーバーに接続できません。ネットワーク接続を確認してください。' });
        }
        setIsTokenValid(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setErrors({ auth: '無効なリセットリンクです' });
      setIsTokenValid(false);
    }
  }, [token]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const newErrors: ResetPasswordErrors = {};
    if (!password) newErrors.password = 'パスワードを入力してください。';
    else if (!validatePassword(password)) newErrors.password = PASSWORD_VALIDATION.ERROR_MESSAGE;
    else if (containsEmoji(password)) newErrors.password = '絵文字は使用できません。';

    if (!passwordConfirmation) newErrors.confirmPassword = 'パスワード（確認）を入力してください。';
    else if (password !== passwordConfirmation) newErrors.confirmPassword = 'パスワードが一致しません';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await axios.post('/reset-password', {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation
      });

      alert(response.data.message);

      navigate('/');
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      if (axiosError.response?.data) {
        setErrors({ auth: axiosError.response.data.message || 'パスワードリセットに失敗しました' });
      } else {
        setErrors({ auth: 'サーバーに接続できません。ネットワーク接続を確認してください。' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isTokenValid && errors.auth) {
    return (
      <div className="blank-container">
        <div className="blank-content">
          <div className="login-form-container">
            <div className="text-center mb-4">
              <Image 
                src="/logo.svg" 
                alt="ESF Logo" 
                className="logo-image" 
              />
              <h4>パスワードリセット</h4>
            </div>

            <div className="text-center">
              {errors.auth}
            </div>

            <div className="text-center mt-4">
                <Link to="/forgot-password" className="register-link me-2">再度リクエスト</Link>
                <Link to="/" className="register-link">ログイン画面に戻る</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blank-container">
      <div className="blank-content">
        <div className="login-form-container">
          <div className="text-center mb-4">
            <Image 
              src="/logo.svg" 
              alt="ESF Logo" 
              className="logo-image" 
            />
            <h4>パスワードリセット</h4>
          </div>

          {message && <Alert variant="success">{message}</Alert>}
          {errors.auth && <Alert variant="danger">{errors.auth}</Alert>}

          {isTokenValid && (
            <>
              <p className="reset-password-text mb-4">
                {companyName} ({email}) のパスワードを再設定します
              </p>

              <Form onSubmit={handleSubmit} className="login-form">
                <Form.Group className="mb-4">
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    isInvalid={!!errors.password}
                    placeholder="新しいパスワード"
                    className="login-input"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                  <div className="d-flex justify-content-between mt-2">
                    <small className="text-muted">{PASSWORD_VALIDATION.SHORT_COMMENT}</small>
                    <span></span>
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Control
                    type="password"
                    value={passwordConfirmation}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPasswordConfirmation(e.target.value)}
                    isInvalid={!!errors.confirmPassword}
                    placeholder="新しいパスワード（確認）"
                    className="login-input"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">確認のため再入力してください</small>
                </div>
                </Form.Group>

                <div className="login-button-container">
                  <Button 
                    variant="success" 
                    type="submit" 
                    className="login-button-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '送信中...' : 'パスワードを変更する'}
                  </Button>
                </div>
              </Form>
            </>
          )}

          <div className="text-center mt-4">
            <Link to="/" className="backlogin-link">
              ログイン画面に戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
